"use client"

import { useState, useEffect } from "react"

/**
 * Hook for monitoring network status
 * Detects online/offline state and network quality
 * 
 * Usage:
 * ```tsx
 * const { isOnline, isSlowConnection } = useNetworkStatus()
 * ```
 */
export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState<boolean>(() => {
    if (typeof navigator !== 'undefined') {
      return navigator.onLine
    }
    return true // Assume online on server
  })

  const [isSlowConnection, setIsSlowConnection] = useState<boolean>(false)

  useEffect(() => {
    if (typeof navigator === 'undefined') {
      return
    }

    // Check connection quality if available
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection

    const updateConnectionStatus = () => {
      if (connection) {
        // Check for slow connection (2G or slow 3G)
        const slowConnectionTypes = ['slow-2g', '2g']
        const effectiveType = connection.effectiveType || connection.effectiveType
        setIsSlowConnection(
          slowConnectionTypes.includes(effectiveType) ||
          (connection.downlink && connection.downlink < 1.5) // Less than 1.5 Mbps
        )
      }
    }

    // Initial check
    updateConnectionStatus()

    // Listen for online/offline events
    const handleOnline = () => {
      setIsOnline(true)
      updateConnectionStatus()
    }

    const handleOffline = () => {
      setIsOnline(false)
      setIsSlowConnection(false)
    }

    // Listen for connection changes (if available)
    const handleConnectionChange = () => {
      updateConnectionStatus()
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    if (connection) {
      connection.addEventListener('change', handleConnectionChange)
    }

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      if (connection) {
        connection.removeEventListener('change', handleConnectionChange)
      }
    }
  }, [])

  return {
    isOnline,
    isSlowConnection,
  }
}




